import { api, decodeVaultBlobResponse } from '../api/client';
import { markVaultUnlocked, refreshSession } from '../app';
import {
  clearStoredVaultVersion,
  getStoredVaultVersion,
  normalizeVaultVersion,
  setStoredVaultVersion,
  syncVaultVersionHighWaterMark,
  unlockVaultWithPassphrase,
} from '../crypto/vault';
import { clearVaultSession, storeVaultSession } from '../crypto/vaultSession';
import { seedInfoReplyKeysFromVault } from '../crypto/vaultPersist';
import { confirmVaultRegression } from '../crypto/pubKeyPinning';
import { enforcePassphraseMaxAge } from '../crypto/passphraseAge';
import {
  hasDeviceRememberEnrollment,
  isDeviceRememberSupported,
  unlockVaultWithDeviceRemember,
  clearDeviceRememberForUser,
} from '../crypto/webauthnDevice';
import { applyI18n, t } from '../i18n';
import { apiErrorMessage } from '../i18n/apiErrorMessage';
import { setCryptoBusy } from '../ui/cryptoBusy';
import { iconImg } from '../ui/icons';
import { attachPassphraseWhitespaceWarning } from '../ui/passphraseWhitespaceWarning';
import { openVaultRecoveryDialog } from '../ui/vaultRecoveryDialog';
import { createHelpTip } from '../help/helpTips';

export function vaultUnlockErrorMessage(cause: unknown): string {
  if (cause instanceof Error) {
    switch (cause.message) {
      case 'vault_version_mismatch':
        return t('vault.unlock.versionMismatch');
      case 'vault_missing_user_priv':
        return t('vault.unlock.missingUserPriv');
      case 'vault_passphrase_invalid':
        return t('vault.unlock.passphraseInvalid');
      default:
        break;
    }
  }
  return apiErrorMessage(cause);
}

function showDevVaultReset(panel: HTMLElement, userId: string): void {
  if (!import.meta.env.DEV || panel.querySelector('#vault-dev-reset')) {
    return;
  }

  const wrap = document.createElement('div');
  wrap.className = 'dev-reset-panel';
  wrap.innerHTML = `
    <p class="muted" data-i18n="vault.unlock.devResetHint"></p>
    <button type="button" class="button secondary" id="vault-dev-reset">
      <span data-i18n="vault.unlock.devReset"></span>
    </button>
  `;
  panel.appendChild(wrap);
  applyI18n(wrap);

  wrap.querySelector('#vault-dev-reset')?.addEventListener('click', () => {
    void (async () => {
      try {
        await api.devResetVault();
        clearStoredVaultVersion(userId);
        clearVaultSession();
        void clearDeviceRememberForUser(userId);
        await refreshSession();
      } catch (cause) {
        console.error('Vault dev reset failed', cause);
      }
    })();
  });
}

type UnlockReady = {
  userId: string;
  blob: ReturnType<typeof decodeVaultBlobResponse>;
  passphraseSetAt: string | null;
};

export function VaultUnlockPage(): HTMLElement {
  const panel = document.createElement('section');
  panel.className = 'center-panel';
  panel.setAttribute('data-help-context', 'vault');

  const heading = document.createElement('h1');
  heading.className = 'panel-title';
  const title = document.createElement('span');
  title.setAttribute('data-i18n', 'vault.unlock.title');
  heading.append(iconImg('unlock', 28), title, createHelpTip('vault'));

  panel.innerHTML = `
    <p class="muted" data-i18n="vault.unlock.subtitle"></p>
    <div class="warning-panel" id="vault-regression-warning" hidden>
      <span class="warning-panel__icon"></span>
      <span data-i18n="vault.regressionWarning"></span>
    </div>
    <form class="form-grid" id="vault-unlock-form" method="post" action="#">
      <label>
        <span data-i18n="vault.unlock.passphrase"></span>
        <input type="password" name="passphrase" required autocomplete="current-password" disabled />
      </label>
      <button type="submit" class="button primary" disabled>
        <span class="button__icon"></span>
        <span data-i18n="vault.unlock.submit"></span>
      </button>
    </form>
    <button type="button" class="button secondary vault-passkey-unlock" id="vault-passkey-unlock" hidden>
      <span class="button__icon"></span>
      <span data-i18n="vault.unlock.passkey"></span>
    </button>
    <p class="error-text" id="vault-unlock-error" hidden></p>
    <p class="vault-recovery-link-wrap">
      <button type="button" class="text-link" id="vault-recovery-open" data-i18n="vault.recovery.openLink"></button>
    </p>
  `;

  panel.prepend(heading);
  panel.querySelector('.warning-panel__icon')?.append(iconImg('warning', 20));
  panel.querySelector('.button__icon')?.append(iconImg('unlock', 18));
  panel.querySelector('#vault-passkey-unlock .button__icon')?.append(iconImg('fingerprint', 18));

  const form = panel.querySelector('#vault-unlock-form') as HTMLFormElement;
  const passphraseInput = form.querySelector('input[name="passphrase"]') as HTMLInputElement;
  attachPassphraseWhitespaceWarning(passphraseInput);
  const submitButton = form.querySelector('button[type="submit"]') as HTMLButtonElement;
  const passkeyButton = panel.querySelector('#vault-passkey-unlock') as HTMLButtonElement;
  const error = panel.querySelector('#vault-unlock-error') as HTMLParagraphElement;
  const regression = panel.querySelector('#vault-regression-warning') as HTMLDivElement;
  const recoveryOpen = panel.querySelector('#vault-recovery-open') as HTMLButtonElement;
  let regressionDetected = false;
  let regressionAcknowledged = false;
  let vaultBlobBytes: Uint8Array | null = null;
  let vaultVersion = 0;
  let ready: UnlockReady | null = null;

  async function finishUnlock(
    unlocked: Awaited<ReturnType<typeof unlockVaultWithPassphrase>>,
    userId: string,
    blobVersion: number,
    passphraseSetAt: string | null,
  ): Promise<void> {
    storeVaultSession(unlocked.vaultKey, unlocked.userPrivateKey);
    seedInfoReplyKeysFromVault(unlocked.plaintext);
    const stored = getStoredVaultVersion(userId);
    if (regressionAcknowledged) {
      setStoredVaultVersion(
        userId,
        Math.max(unlocked.plaintext.vaultVersion, blobVersion),
      );
    } else {
      setStoredVaultVersion(
        userId,
        Math.max(stored ?? 0, unlocked.plaintext.vaultVersion, blobVersion),
      );
    }
    markVaultUnlocked();
    await enforcePassphraseMaxAge({
      passphraseSetAt,
      target: 'vaultPassphrase',
    });
  }

  async function unlockWithPasskey(userId: string): Promise<void> {
    if (!vaultBlobBytes) {
      throw new Error('vault_blob_unavailable');
    }

    if (regressionDetected) {
      const acknowledged = await confirmVaultRegression();
      if (!acknowledged) {
        return;
      }

      regressionAcknowledged = true;
      setStoredVaultVersion(userId, vaultVersion);
      regressionDetected = false;
      regression.hidden = true;
    }

    setCryptoBusy(panel, passkeyButton, true, {
      titleKey: 'vault.unlock.passkeyWorking',
      detailKey: 'vault.unlock.passkeyWorkingDetail',
      icon: 'fingerprint',
    });

    try {
      const unlocked = await unlockVaultWithDeviceRemember(userId, vaultBlobBytes, vaultVersion);
      await finishUnlock(unlocked, userId, vaultVersion, ready?.passphraseSetAt ?? null);
    } finally {
      setCryptoBusy(panel, passkeyButton, false, {
        titleKey: 'vault.unlock.passkeyWorking',
        detailKey: 'vault.unlock.passkeyWorkingDetail',
      });
    }
  }

  // Bind immediately so native GET never serializes the passphrase into the URL
  // while vault blob / session fetches are still in flight.
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const context = ready;
    if (!context) {
      return;
    }

    void (async () => {
      error.hidden = true;
      const passphrase = String(new FormData(form).get('passphrase') ?? '');

      if (regressionDetected) {
        const acknowledged = await confirmVaultRegression();
        if (!acknowledged) {
          return;
        }

        regressionAcknowledged = true;
        setStoredVaultVersion(context.userId, vaultVersion);
        regressionDetected = false;
        regression.hidden = true;
      }

      setCryptoBusy(panel, form, true, {
        titleKey: 'vault.unlock.working',
        detailKey: 'vault.unlock.workingDetail',
        icon: 'hourglass',
      });

      try {
        const unlocked = await unlockVaultWithPassphrase(
          passphrase,
          context.blob.vaultSalt,
          context.blob.vkWrapPassphrase,
          context.blob.vaultBlob,
          context.blob.vaultVersion,
        );

        await finishUnlock(
          unlocked,
          context.userId,
          context.blob.vaultVersion,
          context.passphraseSetAt,
        );
      } catch (cause) {
        console.error('Vault unlock failed', cause);
        error.hidden = false;
        error.textContent = vaultUnlockErrorMessage(cause);
        if (cause instanceof Error && cause.message === 'vault_missing_user_priv') {
          showDevVaultReset(panel, context.userId);
        }
      } finally {
        setCryptoBusy(panel, form, false, {
          titleKey: 'vault.unlock.working',
          detailKey: 'vault.unlock.workingDetail',
        });
      }
    })();
  });

  void (async () => {
    const [status, blobResponse] = await Promise.all([
      api.getVaultStatus(),
      api.getVaultBlob(),
    ]);

    const session = await api.getSession();
    const blob = decodeVaultBlobResponse(blobResponse);
    vaultBlobBytes = blob.vaultBlob;
    vaultVersion = Math.max(
      normalizeVaultVersion(status.vaultVersion),
      normalizeVaultVersion(blob.vaultVersion),
    );
    const regressionState = syncVaultVersionHighWaterMark(session.userId, vaultVersion);
    regressionDetected = regressionState === 'regression';
    regression.hidden = !regressionDetected;

    ready = {
      userId: session.userId,
      blob,
      passphraseSetAt: blob.passphraseSetAt,
    };
    passphraseInput.disabled = false;
    submitButton.disabled = false;

    void (async () => {
      if (
        (await isDeviceRememberSupported()) &&
        (await hasDeviceRememberEnrollment(session.userId))
      ) {
        passkeyButton.hidden = false;
      }
    })();

    recoveryOpen.addEventListener('click', () => {
      openVaultRecoveryDialog(session.userId);
    });

    passkeyButton.addEventListener('click', () => {
      error.hidden = true;
      void unlockWithPasskey(session.userId).catch((cause) => {
        console.error('Vault passkey unlock failed', cause);
        error.hidden = false;
        error.textContent = vaultUnlockErrorMessage(cause);
      });
    });
  })().catch((cause) => {
    console.error('Vault unlock init failed', cause);
    error.hidden = false;
    error.textContent = vaultUnlockErrorMessage(cause);
  });

  applyI18n(panel);
  return panel;
}
